import { RefObject } from 'preact';
import { ViewportConfig } from '../../hooks/UNSAFE_useViewportIntersect';
import { SelectionRangeDetail } from '../../hooks/PRIVATE_useSelection';
import { isKeyDefined } from '../UNSAFE_keys';
import { getClientHints } from '../PRIVATE_clientHints';

/**
 * Returns the key from the item element
 */
const getKey = (item: HTMLElement) => {
  return item.dataset['ojKeyType'] === 'number'
    ? Number(item.dataset['ojKey'])
    : item.dataset['ojKey'];
};

/**
 * A helper function that return the key of the first available item.
 */
export const getFirstVisibleKey = (root: HTMLElement, itemSelector: string) => {
  if (root) {
    const firstItem = root.querySelector(itemSelector);
    if (firstItem) {
      const key = getKey(firstItem as HTMLElement);
      return key;
    }
  }
  return null;
};

/**
 * A helper function to find the element with the specified key.
 * Returns null if the element with key is not found.
 */
export const findElementByKey = <K extends string | number>(
  root: HTMLElement,
  key: K,
  itemSelector: string
) => {
  if (root) {
    const items = root.querySelectorAll(itemSelector);
    for (let i = 0; i < items.length; i++) {
      const thisKey = getKey(items[i] as HTMLElement);
      if (key == (thisKey as K)) {
        return items[i];
      }
    }
  }
  return null;
};

/**
 * A helper function that returns the key of the previous or the next item
 * given the specified current key
 */
export const getPrevNextKey = <K extends string | number>(
  root: HTMLElement | null,
  currentKey: K | undefined,
  isPrev: boolean,
  itemSelector: string
) => {
  return (): K | null => {
    if (isKeyDefined(currentKey) && root) {
      const currItem = findElementByKey(root, currentKey as K, itemSelector);
      if (currItem) {
        const nextItem = isPrev ? currItem.previousElementSibling : currItem.nextElementSibling;
        if (nextItem) {
          const key = getKey(nextItem as HTMLElement) as K;
          if (key != null) {
            return key;
          }
        }
        // no next item or next item is not a valid item
        return null;
      }
      return getFirstVisibleKey(root, itemSelector) as K;
    }
    return null;
  };
};

/**
 * A helper function that returns the key of the previous or the next item by count,
 * given the specified current key
 */
export const getPrevNextKeyByCount = <K, D>(
  data: D[] | null,
  getRowKey: (data: D) => K,
  currentKey?: K,
  count = 0
) => {
  if (data && currentKey != undefined) {
    const currIndex = data.findIndex((d) => {
      return getRowKey(d) === currentKey;
    });
    const nextIndex = currIndex + count;
    if (0 <= nextIndex && nextIndex < data.length) {
      return getRowKey(data[nextIndex]);
    }
    return null;
  }
  return null;
};

/**
 * A helper function to get the key from an element
 */
export const keyExtractor = <K extends string | number>(
  element: HTMLElement,
  itemSelector: string
): K | null => {
  const item = element.closest(itemSelector);
  if (item) {
    const key = getKey(item as HTMLElement) as K;
    return key === undefined ? null : key;
  }
  return null;
};

/**
 * A helper function to that return viewPortConfig to collection components
 */
export const getViewportConfig = (rootRef: RefObject<HTMLElement>, config?: ViewportConfig) => {
  return (
    config ?? {
      scroller: () => {
        return rootRef.current;
      }
    }
  );
};

/**
 * A helper function to handle range selection
 */
export const handleSelectionRange = <K, D>(
  detail: SelectionRangeDetail<K>,
  data: D[],
  getRowKey: (data: D) => K
) => {
  const keys = data.map((value: D) => {
    return getRowKey(value);
  });
  const startIndex = keys.indexOf(detail.value.start);
  const endIndex = keys.indexOf(detail.value.end);
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  if (minIndex === -1) {
    // return a range to fetch so we can find all the keys, maxIndex should not be -1
    return { offset: 0, count: -1 };
  } else {
    // range of keys are in the current viewport, return them
    return keys.slice(minIndex, maxIndex + 1);
  }
};

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if running on a mobile device, false otherwise
 */
export function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}
